import {Injectable} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {
  CaseDetailsDTO,
  CaseParticipantAttorneyDTO,
  CaseParticipantDTO,
  CaseStatus,
  CodeDTO, DeterminationAwardOfCodeDTO, DeterminationAwardOfGuid, FeeWaiverRequest,
  FilingDTO, GetFilingDocDetermination,
  ParticipantStatus,
  ParticipantType,
  PostCaseRequest,
  PostFilingDocDTO,
  PostFilingDTO,
  SsnUpdate,
  UserModel,
} from '../../../types/api/dot-net-api-types/data-contracts';
import {
  ICountyFormGroup,
  IDocumentFormGroup,
  IRespondentSelectionTypes
} from '../new-case-filing-form/new-case-filing-form.service';
import {catchError, EMPTY, forkJoin, Observable, of, switchMap, take} from 'rxjs';
import {
  ALLOWED_MIME_TYPES,
  documentMaxSizeValidator,
  documentMimeTypeValidator,
  MAX_FILE_SIZE
} from '../../../core/validators/validators';
import {IUploadedFile} from '../../../components/common/fsx-file-upload/fsx-file-upload.component';
import {DocumentApiService} from '../../api/document/document-api.service';
import {map} from 'rxjs/operators';
import {minActive} from '../../../core/validators/attorneys.validators';
import {
  ALPHA_NUMERIC_REGEX,
  CASE_ATTENTION_LINE_REGEX,
  CASE_PHONE_NUMBER_REGEX,
  CASE_PRISONER_NO_REGEX,
  CASE_SSN_AND_TIN_REGEX,
  CaseTypeIdEnum, ClaimTypeIdEnum
} from 'src/constants';
import {
  IDeterminationExpenseFormGroup,
  IDeterminationFormGroup,
  IDeterminationVictimizationTypeFormGroup
} from '../existing-case-filing-form/existing-case-filing-form.service';
import {FormUtilityService} from '../form-utility/form-utility.service';
import {UtilitiesService} from '../../utilities/utilities-service.service';
import {minVictimizationTypeSelected} from '../../../core/validators/array.validators';
import {
  docketEntryDeterminationAwardAmountValidator,
  hasDeterminationForm
} from '../../../core/validators/determination-award-amount.validators';
import {NON_US_VALUE} from '../../utilities/us-states/us-states.service';
import {FsxOption, getFsxAutocompleteSelectedValue} from 'fsx-component-library';

export const DEFAULT_GUID = '00000000-0000-0000-0000-000000000000';

export interface ICaseFormService {
  // Form Builder Methods
  buildFormFromCase(caseDetailsDTO: CaseDetailsDTO): FormGroup<ICaseFormGroup>;
  buildCaseGeneralInfo(caseDetailsDTO: CaseDetailsDTO): FormGroup<ICaseGeneralInfoFormGroup>;
  buildConsolidation(caseDetailsDTO: CaseDetailsDTO): FormGroup<ICaseConsolidationFormGroup>;
  buildParties(caseDetailsDTO: CaseDetailsDTO): FormGroup<ICasePartiesFormGroup>;
  buildCaseDocketEntryFromDTO(filingDTO: PostFilingDTO): FormGroup<ICaseDocketEntryFormGroup>;
  // DTO Builder Methods
  buildCaseDetailsDTOFromForm(caseFG: FormGroup<ICaseFormGroup>): Observable<CaseDetailsDTO>;
  buildPostCaseRequestFromForm(caseFG: FormGroup<ICaseFormGroup>): Observable<PostCaseRequest>
  // Add participants methods
  addCaseParticipant(caseParticipantsFA: FormArray<FormGroup<ICaseParticipantFormGroup>>, caseTypeId: string, claimTypeId: string, type?: ParticipantType, caseParticipantDTO?: CaseParticipantDTO): void;
  // Delete participants methods
  deleteCaseParticipant(caseParticipantFG: FormGroup<ICaseParticipantFormGroup>): void;
  // Helper Methods
  addParticipantAttorney(participant: FormGroup<ICaseParticipantFormGroup>): void;
  copyAssistantCounselFG(assistantCounselFG: FormGroup<ICaseParticipantFormGroup>): FormGroup<ICaseParticipantFormGroup>;
  addSurvivingDependant(participantFG: FormGroup<ICaseParticipantFormGroup>): void;
  removeSurvivingDependant(participantFG: FormGroup<ICaseParticipantFormGroup>, index: number): void;
  clearParticipantAddress(participantFG: FormGroup<ICaseParticipantFormGroup>): void;
  clearParticipantEmail(participantFG: FormGroup<ICaseParticipantFormGroup>): void;
  clearParticipantPhone(participantFG: FormGroup<ICaseParticipantFormGroup>): void;
  setParticipantAddressFromUser(participantFG: FormGroup<ICaseParticipantFormGroup>, user: UserModel): void;
  setParticipantPhoneFromUser(participantFG: FormGroup<ICaseParticipantFormGroup>, user: UserModel): void;
  setParticipantPhoneFromUser(participantFG: FormGroup<ICaseParticipantFormGroup>, user: UserModel): void;
  clearParticipantClaimantValues(particiapantFG: FormGroup<ICaseParticipantFormGroup>): void;
  initDeterminationFormGroup(mainDocumentFG: FormGroup<IDocumentFormGroup>): void;
  removeDeterminationFormGroup(mainDocumentFG: FormGroup<IDocumentFormGroup>): void;
  addInvoiceNumber(caseFG: FormGroup<ICaseFormGroup>): void;
  removeInvoiceNumber(caseFG: FormGroup<ICaseFormGroup>, index: number): void;
  clearFileValidators(fileFC: FormControl): void;
  addFileValidators(fileFC: FormControl, allowedMimeTypes: string[]): void;
}

@Injectable({
  providedIn: 'root'
})
export class CaseFormService implements ICaseFormService{
  constructor(private readonly documentApiService: DocumentApiService,
              private readonly fb: FormBuilder,
              private readonly formUtilsService: FormUtilityService,
              private readonly utilitiesService: UtilitiesService) { }

  buildForm(): FormGroup<ICaseFormGroup> {
    return this.fb.group<ICaseFormGroup>({
      id: new FormControl('', {nonNullable: true}),
      caseNum: new FormControl('', {nonNullable: true}),
      caseName: new FormControl('', {nonNullable: true, validators: Validators.required}),
      caseInfo: this.buildCaseGeneralInfo(),
      consolidation: this.buildConsolidation(),
      parties: this.buildParties()
    });
  }

  /**
   * Build the form group for the case form.
   * @param caseDetailsDTO
   */
  buildFormFromCase(caseDetailsDTO: CaseDetailsDTO): FormGroup<ICaseFormGroup> {
    return this.fb.group<ICaseFormGroup>({
      id: new FormControl(caseDetailsDTO.id as string, {nonNullable: true}),
      caseNum: new FormControl(caseDetailsDTO.caseNum as string, {nonNullable: true}),
      caseName: new FormControl(caseDetailsDTO.caseName as string, {nonNullable: true, validators: Validators.required}),
      caseInfo: this.buildCaseGeneralInfo(caseDetailsDTO),
      consolidation: this.buildConsolidation(caseDetailsDTO),
      parties: this.buildParties(caseDetailsDTO)
    });
  }

  /**
   * Build the general information form group for the case form.
   * @param caseDetailsDTO
   */
  buildCaseGeneralInfo(caseDetailsDTO?: CaseDetailsDTO): FormGroup<ICaseGeneralInfoFormGroup> {
    const judgeDTO = caseDetailsDTO?.participants?.find(p => p.type === ParticipantType.Judge && !!p.userId);
    const fg = this.fb.group<ICaseGeneralInfoFormGroup>({
      claimAmount: new FormControl(caseDetailsDTO?.claimAmount?.toString() || '', {nonNullable: true, validators: [Validators.max(9999999999.99)]}),
      feeWaiverApplied: new FormControl(caseDetailsDTO?.feeWaiverRequest?.value === FeeWaiverRequest.Granted || null, {nonNullable: true}),
      voucherAwarded: new FormControl(caseDetailsDTO?.voucherAwarded || false, {nonNullable: true}),
      voucherAwardedDate: new FormControl(caseDetailsDTO?.voucherAwardedDate || null, {nonNullable: true}),
      status: new FormControl(caseDetailsDTO?.status as CaseStatus === CaseStatus.Pending ? CaseStatus.Pending : (caseDetailsDTO?.status || CaseStatus.Active), {nonNullable: true}),
      presidingJudge: this.fb.group({
        id: new FormControl(judgeDTO?.id || DEFAULT_GUID, {nonNullable: true, validators: Validators.required}),
        status: new FormControl(judgeDTO?.status || ParticipantStatus.Active, {nonNullable: true}),
        type: new FormControl(judgeDTO?.type || ParticipantType.Judge, {nonNullable: true}),
        name: new FormControl(judgeDTO?.name || '', {nonNullable: true}),
        address: this.fb.group({
          address1: new FormControl(judgeDTO?.address?.address1 || '', {nonNullable: true}),
          address2: new FormControl(judgeDTO?.address?.address2 || ''),
          city: new FormControl(judgeDTO?.address?.city || '', {nonNullable: true}),
          state: new FormControl(judgeDTO?.address?.state || '', {nonNullable: true}),
          zip: new FormControl(judgeDTO?.address?.zip || '', {nonNullable: true}),
          attention: new FormControl(judgeDTO?.address?.attention || ''),
        } as IPostFilingAddressFormGroup),
        userId: new FormControl(judgeDTO?.userId || DEFAULT_GUID, {nonNullable: true}),
        attorneys: this.fb.array([] as FormGroup<ICaseParticipantAttorneyFormGroup>[]),
        isProse: new FormControl(false, {nonNullable: true}),
        isNAOF: new FormControl(true, {nonNullable: true}),
        inList: new FormControl(false, {nonNullable: true}),
        selection: new FormControl(IRespondentSelectionTypes.None, {nonNullable: true}),
        claimantValues: this.fb.group({} as ICaseClaimantFormGroup)
      }) as FormGroup<ICaseParticipantFormGroup>,
      caseType: this.fb.group({
        name: new FormControl(caseDetailsDTO?.caseType?.name || '', {nonNullable: true}),
        id: new FormControl(caseDetailsDTO?.caseType?.id || '', {nonNullable: true, validators: Validators.required}),
      }),
      claimType: this.fb.group({
        name: new FormControl(caseDetailsDTO?.claimType?.name || '', {nonNullable: true}),
        id: new FormControl(caseDetailsDTO?.claimType?.id || '', {nonNullable: true}),
      }),
      dateFiled: new FormControl(caseDetailsDTO?.dateFiled || null),
      dateClosed: new FormControl(caseDetailsDTO?.dateClosed || null),
      dateReopened: new FormControl(caseDetailsDTO?.dateReopened || null),
      incidentDate: new FormControl(caseDetailsDTO?.incidentDate || null),
      suspenseDate: new FormControl(caseDetailsDTO?.suspenseDate || null),
      staff: this.fb.array([] as FormGroup<ICaseParticipantFormGroup>[]),
      county: this.fb.group({
        name: new FormControl('None', {nonNullable: true}),
        id: new FormControl('', {nonNullable: true}),
      } as ICaseDataItemFormGroup),
      agency: this.fb.group({
        id: new FormControl(caseDetailsDTO?.agency?.id || '', {validators: !!caseDetailsDTO?.agency?.id ? Validators.required : []}),
        name: new FormControl(caseDetailsDTO?.agency?.name !== 'None' ? caseDetailsDTO?.agency?.name : '')
      } as ICaseDataItemFormGroup),
      subStatus: this.fb.group({
        id: new FormControl(caseDetailsDTO?.subStatus?.id || ''),
        name: new FormControl(caseDetailsDTO?.subStatus?.name || '')
      } as ICaseDataItemFormGroup),
      prison: this.fb.group({
        id: new FormControl(caseDetailsDTO?.prisonCode?.id || null),
        name: new FormControl(caseDetailsDTO?.prisonCode?.name || '')
      } as ICaseDataItemFormGroup),
      voucherAwardedAmount: new FormControl(caseDetailsDTO?.voucherAwardedAmount || 0),
      invoiceNums: this.fb.array([] as FormControl<string | null>[])
    });
    if (!!caseDetailsDTO) {
      // Set counsel
      const counselParticipants = caseDetailsDTO.participants?.filter(p => p.type === ParticipantType.Staff && p.status === ParticipantStatus.Active);
      this.setAssistantCounsel(fg.controls.staff, counselParticipants || [], caseDetailsDTO.caseType?.id as string, caseDetailsDTO.claimType?.id as string);

      caseDetailsDTO.invoiceNums?.forEach(invoiceNum => {
        fg.controls.invoiceNums.push(new FormControl(invoiceNum || '', {nonNullable: true, validators: [Validators.pattern(ALPHA_NUMERIC_REGEX), Validators.maxLength(20)]}));
      });
    }
    return fg;
  }

  /**
   * Helper method to set counsel.
   * @param counselFA
   * @param existingCounsel
   * @param caseTypeId
   * @param claimTypeId
   */
  setAssistantCounsel(counselFA: FormArray<FormGroup<ICaseParticipantFormGroup>>, existingCounsel: CaseParticipantDTO[], caseTypeId: string, claimTypeId: string): void {
    existingCounsel.forEach(counsel => {
      this.addCaseParticipant(counselFA, caseTypeId, claimTypeId, ParticipantType.Staff, counsel);
    });
  }

  /**
   * Build the consolidation form group for the case form.
   * @param caseDetailsDTO
   */
  buildConsolidation(caseDetailsDTO?: CaseDetailsDTO): FormGroup<ICaseConsolidationFormGroup> {
    return this.fb.group({
      isConsolidated: new FormControl(caseDetailsDTO?.isConsolidated || false),
      consolidatedCaseNum: new FormControl(caseDetailsDTO?.consolidatedCaseNum || ''),
    } as ICaseConsolidationFormGroup);
  }

  /**
   * Build the parties form group for the case form.
   * @param caseDetailsDTO
   */
  buildParties(caseDetailsDTO?: CaseDetailsDTO): FormGroup<ICasePartiesFormGroup> {
    // ToDo: 2/26/2025 - After initial setup, need to go through, and decide what validation needs to remain or be removed.
    const caseTypeId = caseDetailsDTO?.caseType?.id as string;
    const claimTypeId = caseDetailsDTO?.claimType?.id as string;

    // Init parties form group
    const partiesFG = this.fb.group({
      claimants: this.fb.array([] as FormGroup<ICaseParticipantFormGroup>[]),
      respondents: this.fb.array([] as FormGroup<ICaseParticipantFormGroup>[]),
    } as ICasePartiesFormGroup);
    // Add participants to parties form group
    caseDetailsDTO?.participants?.forEach(p => {
      switch (p.type) {
        case ParticipantType.Claimant:
          this.addCaseParticipant(partiesFG.controls.claimants, caseTypeId, claimTypeId, ParticipantType.Claimant, p);
          break;
        case ParticipantType.Respondant:
          this.addCaseParticipant(partiesFG.controls.respondents, caseTypeId, claimTypeId, ParticipantType.Respondant, p);
          break;
      }
    });
    return partiesFG;
  }

  /**
   * A helper method that removes/adds validators to the claimants form array.
   * @param claimantsFA
   * @param caseTypeId
   * @param claimTypeId
   * @private
   */
  private applyClaimantValidators(claimantsFA: FormArray<FormGroup<ICaseParticipantFormGroup>>, caseTypeId: string, claimTypeId: string): void {
    // If is a general case, need to remove validators specific to crime victim.
     if (caseTypeId === CaseTypeIdEnum.General) {
      claimantsFA.controls.forEach(claimantFG => {
        const survivingDependants = claimantFG.controls.claimantValues.controls.survivingDependants.controls;
        const warrantNumbers = claimantFG.controls.claimantValues.controls.warrantNums.controls;

        // Clear validators
        this.formUtilsService.clearValidators([
          // General CV Values (Race, Gender, Number of Minors, Age range, Victim Name)
          claimantFG.controls.claimantValues.controls.race,
          claimantFG.controls.claimantValues.controls.gender,
          claimantFG.controls.claimantValues.controls.numberOfMinors,
          claimantFG.controls.claimantValues.controls.ageRange,
          claimantFG.controls.claimantValues.controls.crimeVictimName,
          claimantFG.controls.claimantValues.controls.survivingDependantName,
        ], true);

        // Reset form controls
        this.formUtilsService.resetFormControls([
          // General CV Values (Race, Gender, Number of Minors, Age range, Victim Name)
          claimantFG.controls.claimantValues.controls.race,
          claimantFG.controls.claimantValues.controls.gender,
          claimantFG.controls.claimantValues.controls.numberOfMinors,
          claimantFG.controls.claimantValues.controls.ageRange,
          claimantFG.controls.claimantValues.controls.crimeVictimName,
          claimantFG.controls.claimantValues.controls.survivingDependantName,
        ]);
        claimantFG.updateValueAndValidity();
      });
      claimantsFA.updateValueAndValidity();
    }
  }

  /**
   * A helper method that removes/adds validators to the claimants form array.
   * @param claimantFG
   * @param isGeneral
   * @param claimTypeId
   * @private
   */
  public applyClaimantValidator(claimantFG: FormGroup<ICaseParticipantFormGroup>, isGeneral: boolean, claimTypeId: string): void {
    if (isGeneral) {
      const survivingDependants = claimantFG.controls.claimantValues.controls.survivingDependants.controls;
      const warrantNumbers = claimantFG.controls.claimantValues.controls.warrantNums.controls;

      // Clear values
      claimantFG.controls.claimantValues.controls.race.setValue('');
      claimantFG.controls.claimantValues.controls.gender.setValue('');
      claimantFG.controls.claimantValues.controls.numberOfMinors.setValue(0);
      claimantFG.controls.claimantValues.controls.ageRange.setValue('');
      claimantFG.controls.claimantValues.controls.crimeVictimName.setValue('');
      claimantFG.controls.claimantValues.controls.survivingDependantName.setValue('');

      // Clear validators
      this.formUtilsService.clearValidators([
        // General CV Values (Race, Gender, Number of Minors, Age range, Victim Name)
        claimantFG.controls.claimantValues.controls.race,
        claimantFG.controls.claimantValues.controls.gender,
        claimantFG.controls.claimantValues.controls.numberOfMinors,
        claimantFG.controls.claimantValues.controls.ageRange,
        claimantFG.controls.claimantValues.controls.crimeVictimName,
        claimantFG.controls.claimantValues.controls.survivingDependantName,
      ], true);

      // Reset form controls
      this.formUtilsService.resetFormControls([
        // General CV Values (Race, Gender, Number of Minors, Age range, Victim Name)
        claimantFG.controls.claimantValues.controls.race,
        claimantFG.controls.claimantValues.controls.gender,
        claimantFG.controls.claimantValues.controls.numberOfMinors,
        claimantFG.controls.claimantValues.controls.ageRange
      ]);
    } else {
      this.formUtilsService.addValidators([
        {control: claimantFG.controls.claimantValues.controls.race, validators: Validators.required},
        {control: claimantFG.controls.claimantValues.controls.gender, validators: Validators.required},
        {control: claimantFG.controls.claimantValues.controls.numberOfMinors, validators: [Validators.required, Validators.min(0), Validators.max(9)]},
        {control: claimantFG.controls.claimantValues.controls.ageRange, validators: Validators.required}
      ]);
    }
    claimantFG.updateValueAndValidity();
  }

  /**
   * Build a case details DTO from the form group.
   * @param caseFG
   */
  buildCaseDetailsDTOFromForm(caseFG: FormGroup<ICaseFormGroup>): Observable<CaseDetailsDTO> {
    const caseTypeId = caseFG.value.caseInfo?.caseType?.id;
    const claimTypeId = caseFG.value.caseInfo?.claimType?.id;

    const caseDetailsDTO: CaseDetailsDTO = {
      id: caseFG.value.id,
      caseNum: caseFG.value.caseNum,
      status: Number(caseFG.controls.caseInfo.controls.status.value),
      caseName: caseFG.value.caseName,
      caseType: {
        id: (caseTypeId !== '' && caseTypeId !== 'null') ? caseTypeId : null,
        name: caseFG.value.caseInfo?.caseType?.name,
      },
      claimType: {
        id: (claimTypeId !== '' && claimTypeId !== 'null') ? claimTypeId : null,
        name: caseFG.value.caseInfo?.claimType?.name,
      },
      claimAmount: caseFG.value.caseInfo?.caseType?.id === CaseTypeIdEnum.General ?
        (Number(caseFG.value.caseInfo.claimAmount) || null) : null,
      dateFiled: caseFG.value.caseInfo?.dateFiled,
      dateClosed: caseFG.value.caseInfo?.dateClosed,
      dateReopened: caseFG.value.caseInfo?.dateReopened,
      incidentDate: caseFG.value.caseInfo?.incidentDate,
      suspenseDate: caseFG.value.caseInfo?.suspenseDate,
      isConsolidated: caseFG.value.consolidation?.isConsolidated,
      consolidatedCaseNum: caseFG.value.consolidation?.consolidatedCaseNum,
      participants: this.buildCaseParticipantDTOs(caseFG),
      voucherAwarded: caseFG.value.caseInfo?.voucherAwarded,
      voucherAwardedDate: caseFG.value.caseInfo?.voucherAwardedDate,
      voucherAwardedAmount: caseFG.value.caseInfo?.voucherAwardedAmount || null,
      agency: {
        id: caseFG.value.caseInfo?.agency?.id !== '' ? caseFG.value.caseInfo?.agency?.id : null,
        name: caseFG.value.caseInfo?.agency?.name || 'None'
      },
      prisonCode: {
        id: caseFG.value.caseInfo?.prison?.id !== '' && caseFG.value.caseInfo?.prison?.id !== 'null' ? caseFG.value.caseInfo?.prison?.id : null,
        name: 'None'
      },
      subStatus: {
        id: caseFG.value.caseInfo?.subStatus?.id !== '' && caseFG.value.caseInfo?.subStatus?.id !== 'null' ? caseFG.value.caseInfo?.subStatus?.id : null,
        name: ''
      },
      // Following items below, need to have empty values to be able to save
      feeWaiverRequest: {
        label: ''
      },
      invoiceNums: caseFG.value.caseInfo?.invoiceNums?.map(v => v as string) || []
    }
    return of(caseDetailsDTO);
  }

  /**
   * Build a post case request from the form group.
   * @param caseFG
   */
  buildPostCaseRequestFromForm(caseFG: FormGroup<ICaseFormGroup>): Observable<PostCaseRequest> {
    const casePostRequest: PostCaseRequest = {
      agencyId: caseFG.value.caseInfo?.agency?.id || null,
      caseName: caseFG.value.caseName,
      caseStatus: Number(caseFG.controls.caseInfo.controls.status.value),
      participants: this.buildCaseParticipantDTOs(caseFG),
      caseType: (caseFG.value.caseInfo?.caseType?.id || null) as string,
      claimTypeId: caseFG.value.caseInfo?.claimType?.id || null,
      isConsolidatedCase: caseFG.value.consolidation?.isConsolidated,
      consolidatedCaseNum: caseFG.value.consolidation?.consolidatedCaseNum,
      dateFiled: caseFG.value.caseInfo?.dateFiled,
      invoiceNums: caseFG.value.caseInfo?.invoiceNums?.map(v => v as string) || [],
      claimAmount: caseFG.value.caseInfo?.caseType?.id === CaseTypeIdEnum.General ?
        (Number(caseFG.value.caseInfo.claimAmount) || null) : null,
    }
    return of(casePostRequest);
  }

  /**
   * Build the case participant DTOs from the form group.
   * @param docketFG
   * @param judgeFG
   */
  buildCaseParticipantDTOs(docketFG: FormGroup<ICaseFormGroup>): CaseParticipantDTO[] {
  // .controls.parties, caseFG.controls.generalInfo.controls.presidingJudge
    let participants: CaseParticipantDTO[] = [];
    // Claimants
    participants = participants.concat(this.buildCaseParticipantDTO(docketFG.controls.parties.controls.claimants));
    // Respondents
    participants = participants.concat(this.buildCaseParticipantDTO(docketFG.controls.parties.controls.respondents));
    // Judges
    const judgeFG = docketFG.controls.caseInfo.controls.presidingJudge;
    if ((!!judgeFG.value.id && !!judgeFG.value.userId) && (judgeFG.value.id !== judgeFG.value.userId)) {
      participants = participants.concat(this.buildCaseParticipantDTO(this.fb.array([judgeFG])));
    }
    // Assistant Counsel
    participants = participants.concat(this.buildCaseParticipantDTO(docketFG.controls.caseInfo.controls.staff));
    return participants;
  }

  /**
   * Build the case participant attorneys DTOs from the form array.
   * @param particiapntsFA
   * @private
   */
  private buildCaseParticipantDTO(particiapntsFA: FormArray<FormGroup<ICaseParticipantFormGroup>>): CaseParticipantDTO[] {
    const participants: CaseParticipantDTO[] = [];
    particiapntsFA.value.forEach(participant => {
      const participantDTO: CaseParticipantDTO = {
        id: participant.id || DEFAULT_GUID,
        status: participant.status === null ? undefined : Number(participant.status) as ParticipantStatus,
        type: participant.type,
        name: participant.name,
        address: {
          attention: participant.address?.attention || null,
          address1: participant.address?.address1,
          address2: participant.address?.address2,
          city: participant.address?.city,
          state: participant.address?.state,
          zip: participant.address?.zip,
        },
        userId: getFsxAutocompleteSelectedValue(participant.userId as string),
        isProSe: participant.isProse || false,
        attorneys: [],
        // ILSOS Values
        ageRangeCode: participant.claimantValues?.ageRange ? {
          id: participant.claimantValues?.ageRange,
        } as CodeDTO : null,
        survivingDependant: participant.claimantValues?.survivingDependantName || null,
        county: !!participant.claimantValues?.county?.id && participant.claimantValues.county.id !== 'null' ? {
          id: participant.claimantValues?.county?.id,
        } as CodeDTO : null,
        cvName: participant.claimantValues?.crimeVictimName || null,
        decedentName: participant.claimantValues?.decedent || null,
        claimantEmail: participant.claimantValues?.email || null,
        genderCode: !! participant.claimantValues?.gender ? {
          id: participant.claimantValues?.gender,
        } as CodeDTO : null,
        dependentCount: participant.claimantValues?.numberOfMinors || null,
        prisonerNum: participant.claimantValues?.prisonerNum || null,
        claimantRaceCode: !!participant.claimantValues?.race && participant.claimantValues.race !== 'null' ? {
          id: participant.claimantValues.race
        } as CodeDTO : null,
        warrantNum: participant.claimantValues?.warrantNum || null,
        phoneNumber: participant.claimantValues?.phoneNum || null
      };

      // Setup Surviving Dependants
      const survivingDependants = participant.claimantValues?.survivingDependants?.filter(sv => !!sv && sv !== '') as string[] || [];
      participantDTO.survivingDependants = survivingDependants.length ? survivingDependants : [];

      // Setup Warrant Numbers when applicable
      const warrantNumbers = participant.claimantValues?.warrantNums?.filter(wn => !!wn && wn !== '') as string[] || [];
      participantDTO.warrantNums = warrantNumbers.length ? warrantNumbers : [];

      // Update SSN if needed
      if (participant.claimantValues?.ssn !== '') {
        participantDTO.ssnUpdate = {
          newSSN: Number(this.utilitiesService.removeNonNumericCharacters(participant.claimantValues?.ssn || '')) || null
        } as SsnUpdate;
      }

      if (participantDTO.type === ParticipantType.Judge || participantDTO.type === ParticipantType.Staff) {
        // ToDo: this extra check was missing as a result of a change on the BE. Without this, wonky behavior occurs.
        if (participantDTO.type === ParticipantType.Judge) {
          participantDTO.id = null;
        }
        participantDTO.userId = participant.userId;
      }
      const participantAttorneys: CaseParticipantAttorneyDTO[] = [];
      participant.attorneys?.forEach(attorney => {
        const attorneyDTO: CaseParticipantAttorneyDTO = {
          id: attorney.id,
          attorneyId: attorney.attorneyId,
          userId: attorney.userId,
          fName: attorney.firstName,
          mName: attorney.middleName,
          lName: attorney.lastName,
          email: attorney.email,
          firmName: attorney.firmName,
          address: {
            address1: attorney.address?.address1,
            address2: attorney.address?.address2,
            city: attorney.address?.city,
            state: attorney.address?.state,
            zip: attorney.address?.zip,
          },
          phoneNumber: attorney.phoneNumber,
          status: Number(attorney.status),
        }
        participantAttorneys.push(attorneyDTO);
      });
      participantDTO.attorneys = participantAttorneys;

      if(participantDTO.attorneys?.length == 0 && !participantDTO.isProSe)
        participantDTO.isNAOF = true;

      participants.push(participantDTO);
    });
    return participants;
  }

  /**
   * Builds the docket entry form group from a saved filing: its fields, main and supporting documents, and the main
   * document's saved DL/IR determination (any determination with a category, including Award No Pay, which has no
   * determination id).
   * @param filingDTO the docket entry to build the form from
   * @returns the docket entry form group
   */
  buildCaseDocketEntryFromDTO(filingDTO: FilingDTO): FormGroup<ICaseDocketEntryFormGroup> {
    const entryFG = this.fb.group<ICaseDocketEntryFormGroup>({
      filingId: new FormControl(filingDTO.filingId as string || null, {nonNullable: true}),
      filingDateTime: new FormControl(filingDTO.dateSubmitted as string || new Date().toISOString(), {nonNullable: true}),
      description: new FormControl(filingDTO.description || '', {nonNullable: true, validators: Validators.required}),
      mainDocument: this.fb.group({
        documentId: new FormControl('', {nonNullable: true}),
        file: new FormControl(null, {nonNullable: false, validators: [documentMimeTypeValidator(ALLOWED_MIME_TYPES), documentMaxSizeValidator(MAX_FILE_SIZE)]}),
        fileName: new FormControl('', {nonNullable: true}),
        displayName: new FormControl('', {nonNullable: true}),
        description: new FormControl('', {nonNullable: true}),
        secured: new FormControl(false),
        status: new FormControl(ParticipantStatus.Active, {nonNullable: true}),
        uri: new FormControl('' as string),
        docTypeId: new FormControl('', {nonNullable: true, validators: Validators.required}),
        // Empty placeholder until a determination is loaded below; detect it with hasDeterminationForm().
        determination: this.fb.group({} as IDeterminationFormGroup)
      } as IDocumentFormGroup),
      supportingDocuments: this.fb.array([] as FormGroup<IDocumentFormGroup>[]),
      // @ts-ignore
      ehbCode: new FormControl(filingDTO?.dispositionId || '', {nonNullable: true}),
      commonwealthCode: new FormControl(filingDTO?.dispositionCommonwealthId || '', {nonNullable: true}),
    }, {validators: [docketEntryDeterminationAwardAmountValidator()]});
    // Set any values from the draft filing if it exists.
    if (!!filingDTO) {
      filingDTO.documents?.forEach(documentDTO => {
        if (documentDTO.mainDocument) {
          // Set values FIRST so addMainDocumentValidators can detect existing document via documentId/uri.
          entryFG.controls.mainDocument.controls.documentId.setValue(documentDTO.documentId as string),
          entryFG.controls.mainDocument.controls.fileName.setValue(documentDTO.fileName as string);
          entryFG.controls.mainDocument.controls.displayName.setValue(documentDTO.displayName as string);
          entryFG.controls.mainDocument.controls.description.setValue(documentDTO.description as string);
          entryFG.controls.mainDocument.controls.uri?.setValue(documentDTO.fileURI as string);
          entryFG.controls.mainDocument.controls.status.setValue(documentDTO.status || ParticipantStatus.Active);
          entryFG.controls.mainDocument.controls.docTypeId.setValue(documentDTO.docType as string);
          // Add validators after values set. addMainDocumentValidators skips file.required when documentId/uri already present.
          this.addMainDocumentValidators(entryFG.controls.mainDocument);
          entryFG.controls.mainDocument.controls.file.setErrors(null);
          entryFG.controls.mainDocument.controls.file.updateValueAndValidity();

          const determination = documentDTO.determination as GetFilingDocDetermination;
          // Award No Pay determinations have no determination id, so load any determination that has a category.
          if (!!determination && (!!determination.determination?.id || !!determination.determinationCategory?.id)) {
            entryFG.controls.mainDocument.controls.determination = this.fb.group({
              category: new FormControl(determination.determinationCategory?.id || '', {validators: Validators.required}),
              supplementalNumber: new FormControl(determination.supplementalDeterminationNum || -1, {nonNullable: true}),
              determination: new FormControl(determination.determination?.id ?? null, {validators: Validators.required}),
              victimizationTypes: this.fb.array([] as FormGroup<IDeterminationVictimizationTypeFormGroup>[], {validators: minVictimizationTypeSelected(1)}),
              awardTypes: this.fb.array([] as FormGroup<IDeterminationExpenseFormGroup>[]),
              totalAwardAmount: new FormControl(filingDTO.awardAmount || 0, {nonNullable: true}),
            } as IDeterminationFormGroup);

            // Add the victimization types to the form array.
            const vts = determination.determinationVictimTypes as CodeDTO[] || [];
            vts.forEach(vt => {
              entryFG.controls.mainDocument.controls.determination?.controls.victimizationTypes.push(this.fb.group({
                value: new FormControl(vt.id as string, {nonNullable: true}),
                checked: new FormControl(true, {nonNullable: true}),
                name: new FormControl(vt.name as string, {nonNullable: true}),
              } as IDeterminationVictimizationTypeFormGroup));
            });

            // Add the expenses to the form array.
            let awardValues = determination.determinationAwards as DeterminationAwardOfCodeDTO[] || [];
            awardValues.forEach(av => {
              entryFG.controls.mainDocument.controls.determination?.controls.awardTypes.push(this.fb.group({
                amount: new FormControl(av.awardValue || 0, {nonNullable: true}),
                typeId: new FormControl(av.award?.id || '', {nonNullable: true}),
                name: new FormControl(av.award?.name || '', {nonNullable: true}),
              } as IDeterminationExpenseFormGroup));
            });
            entryFG.controls.mainDocument.updateValueAndValidity();
          }
        } else {
          const hasExistingFile = !!documentDTO.documentId || !!documentDTO.fileURI;
          const fileValidators = hasExistingFile
            ? [documentMimeTypeValidator(ALLOWED_MIME_TYPES), documentMaxSizeValidator(MAX_FILE_SIZE)]
            : [Validators.required, documentMimeTypeValidator(ALLOWED_MIME_TYPES), documentMaxSizeValidator(MAX_FILE_SIZE)];
          const supportingDocFG = this.fb.group({
            documentId: new FormControl(documentDTO.documentId as string, {nonNullable: true}),
            file: new FormControl(null, {nonNullable: false, validators: fileValidators}),
            fileName: new FormControl(documentDTO.fileName, {nonNullable: true, validators: Validators.required}),
            displayName: new FormControl(documentDTO.displayName, {nonNullable: true, validators: Validators.required}),
            description: new FormControl(documentDTO.description, {nonNullable: true, validators: Validators.required}),
            uri: new FormControl(documentDTO.fileURI),
            secured: new FormControl(documentDTO.secured || false),
            status: new FormControl(documentDTO.status || ParticipantStatus.Active),
            docTypeId: new FormControl(documentDTO.docType as string, {nonNullable: true, validators: Validators.required}),
            // Empty placeholder (supporting documents have no determination); detect it with hasDeterminationForm().
            determination: this.fb.group({} as IDeterminationFormGroup),
            docType: new FormControl(documentDTO.docType as string, {nonNullable: true}),
          } as IDocumentFormGroup);
          supportingDocFG.controls.file.setErrors(null);
          entryFG.controls.supportingDocuments.push(supportingDocFG);
        }
      });
      entryFG.controls.filingDateTime.setValue(filingDTO.dateSubmitted as string);
    }
    entryFG.updateValueAndValidity();
    return entryFG;
  }

  /**
   * Add a case participant to the form array.
   * @param formArray
   * @param type
   * @param caseTypeId
   * @param claimTypeId
   * @param caseParticipantDTO
   * @param includeValidator
   */
  addCaseParticipant(formArray: FormArray<FormGroup<ICaseParticipantFormGroup>>, caseTypeId: string, claimTypeId: string, type?: ParticipantType, caseParticipantDTO?: CaseParticipantDTO, includeValidator?: boolean, isOther = false): void {
    const nameMap: Record<ParticipantType, string> = {
      [ParticipantType.Claimant]: 'New Claimant',
      [ParticipantType.Respondant]: isOther ? IRespondentSelectionTypes.Other : IRespondentSelectionTypes.StateOfIllinois,
      [ParticipantType.Judge]: 'New Judge',
      [ParticipantType.Staff]: 'New Staff',
    };

    const normalize = (str: string) => str.toLowerCase().replace(/\s/g, '');
    const nameValue = caseParticipantDTO?.name || nameMap[type as ParticipantType] || '';
    const isStateOfIllinois = normalize(nameValue) === normalize(IRespondentSelectionTypes.StateOfIllinois);

    const selection = isStateOfIllinois
      ? IRespondentSelectionTypes.StateOfIllinois
      : IRespondentSelectionTypes.Other;

    const isClaimantCheck = caseParticipantDTO?.type === ParticipantType.Claimant || type === ParticipantType.Claimant;
    const isNonUSCheck = caseParticipantDTO?.address?.state === 'Non-US';
    const isCVCheck = caseTypeId === CaseTypeIdEnum.CrimeVictim;
    const newClaimantNAOF = type === ParticipantType.Claimant && !caseParticipantDTO || caseParticipantDTO?.isNAOF;

    // Build the form group
    const fg = this.fb.group({
      id: new FormControl(caseParticipantDTO?.id || DEFAULT_GUID, {nonNullable: true}),
      status: new FormControl(caseParticipantDTO?.status || ParticipantStatus.Active, {nonNullable: true}),
      type: new FormControl(type),
      name: new FormControl(caseParticipantDTO?.name || nameValue, {
        nonNullable: true,
        validators: Validators.required
      }),
      address: this.fb.group({
        attention: new FormControl(caseParticipantDTO?.address?.attention || '', {
          validators: [
            Validators.pattern(CASE_ATTENTION_LINE_REGEX),
            Validators.maxLength(50)
          ]
        }),
        address1: new FormControl(caseParticipantDTO?.address?.address1 || '', {
          nonNullable: true,
          validators: isClaimantCheck ? [
            Validators.required
          ] : []
        }),
        address2: new FormControl(caseParticipantDTO?.address?.address2 || ''),
        city: new FormControl(caseParticipantDTO?.address?.city || '', {
          nonNullable: true,
          validators: isClaimantCheck ? [
            Validators.required
          ] : []
        }),
        state: new FormControl(caseParticipantDTO?.address?.state || '', {
          nonNullable: true,
          validators: isClaimantCheck ? [
            Validators.required
          ] : []
        }),
        zip: new FormControl(caseParticipantDTO?.address?.zip || '', {
          nonNullable: true,
          validators: isClaimantCheck && !isNonUSCheck ? [
            Validators.minLength(5),
            Validators.maxLength(5)
          ] : []
        }),
      } as IPostFilingAddressFormGroup),
      userId: new FormControl(caseParticipantDTO?.userId || null, {nonNullable: true}),
      attorneys: this.fb.array([] as FormGroup<ICaseParticipantAttorneyFormGroup>[], {validators: includeValidator ? [minActive(1)] : []}),
      isProse: new FormControl(caseParticipantDTO?.isProSe || false, {nonNullable: true}),
      isNAOF: new FormControl(caseParticipantDTO?.isNAOF || newClaimantNAOF, {nonNullable: true}),
      inList: new FormControl((caseParticipantDTO?.userId === null && caseParticipantDTO.isProSe) || false, {nonNullable: true}),
      selection: new FormControl(selection, {nonNullable: true, validators: Validators.required}),
      claimantValues: this.fb.group({
        ageRange: new FormControl(caseParticipantDTO?.ageRangeCode?.id || '', {
          validators: isClaimantCheck && isCVCheck ? [
            Validators.required
          ] : []
        }),
        county: this.fb.group({
          id: new FormControl(caseParticipantDTO?.county?.id || ''),
          name: new FormControl(caseParticipantDTO?.county?.name || ''),
        } as ICountyFormGroup),
        crimeVictimName: new FormControl(caseParticipantDTO?.cvName || ''),
        decedent: new FormControl(caseParticipantDTO?.decedentName || ''),
        email: new FormControl(caseParticipantDTO?.claimantEmail || '', {
          validators: [
            Validators.email
          ]
        }),
        gender: new FormControl(caseParticipantDTO?.genderCode?.id || '', {
          validators: isClaimantCheck && isCVCheck ? [
            Validators.required
          ] : []
        }),
        numberOfMinors: new FormControl(caseParticipantDTO?.dependentCount || 0, {
          validators: isClaimantCheck && isCVCheck ? [
            Validators.required,
            Validators.min(0),
            Validators.max(9)
          ] : []
        }),
        // ToDo: 2/25/2025 - Need to convert to FormArray
        prisonerNum: new FormControl(caseParticipantDTO?.prisonerNum || '', {
          validators: isClaimantCheck && !isCVCheck ? [
            Validators.pattern(CASE_PRISONER_NO_REGEX),
            Validators.maxLength(15)
          ] : []
        }),
        phoneNum: new FormControl(caseParticipantDTO?.phoneNumber || '', {
          validators: (isClaimantCheck && !isCVCheck && claimTypeId !== ClaimTypeIdEnum.Prisoner) ? [
            Validators.pattern(CASE_PHONE_NUMBER_REGEX)
          ] : []
        }),
        race: new FormControl(caseParticipantDTO?.claimantRaceCode?.id || '', {
          validators: isClaimantCheck && isCVCheck ? [
            Validators.required
          ] : []
        }),
        ssn: new FormControl('', {
          nonNullable: true,
          validators: [
            Validators.pattern(CASE_SSN_AND_TIN_REGEX)
          ]
        }),
        ssnRedacted: new FormControl(caseParticipantDTO?.ssn || ''),
        survivingDependantName: new FormControl(caseParticipantDTO?.survivingDependant || ''),
        survivingDependants: this.fb.array([] as FormControl<string>[]),
        // ToDo: 2/25/2025 - Need to convert to FormArray
        warrantNum: new FormControl(caseParticipantDTO?.warrantNum || '', {
          validators: [
            Validators.pattern(ALPHA_NUMERIC_REGEX),
            Validators.maxLength(10)
          ]
        }),
        warrantNums: this.fb.array([] as FormControl<string>[])
      } as ICaseClaimantFormGroup)
    }) as FormGroup<ICaseParticipantFormGroup>;
    if (fg.value.address?.state !== NON_US_VALUE) {
      fg.controls.address.controls.zip.addValidators(Validators.required);
    }

    // Add the surviving dependants
    caseParticipantDTO?.survivingDependants?.forEach(survivingDependant => {
      if (survivingDependant !== '') {
        fg.controls.claimantValues.controls.survivingDependants.push(new FormControl(survivingDependant || ''));
      }
    });

    // Add the warrant numbers
    caseParticipantDTO?.warrantNums?.forEach(warrantNum => {
      if (warrantNum !== '') {
        fg.controls.claimantValues.controls.warrantNums.push(new FormControl(warrantNum || '', {
          validators: [
            Validators.pattern(ALPHA_NUMERIC_REGEX),
            Validators.maxLength(10)
          ]
        }));
      }
    });

    // ToDo: If prose, need to set the value as such.

    // Check if DEP
    if (caseParticipantDTO?.name === 'Department of Environmental Protection (DEP)') {
      fg.controls.selection.setValue(IRespondentSelectionTypes.StateOfIllinois);
    }
    // Build the attorneys
    caseParticipantDTO?.attorneys?.forEach(attorney => {
      this.addParticipantAttorney(fg, attorney);
    });
    // Check if need to add required validator for attorneys, and min 1
    if (includeValidator && !newClaimantNAOF) {
      this.addAttorneyValidators(fg)
    }
    // If new claimant NAOF, need to remove the attorney validators
    if(newClaimantNAOF) {
      this.removeAttorneyValidators(fg);
    }

    // Push the FG
    formArray.push(fg);
    // ToDo: 2/27/2025 - This is not efficient. Need to revisit this part, but due to complexity, deadline, and time crunch, need to get this working and move on for now.
    // Update the form group validity depending on case type
    this.applyClaimantValidators(formArray, caseTypeId, claimTypeId);
  }

  /**
   * Add the attorney validators to the participant form group.
   * @param participantFG
   */
  addAttorneyValidators(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    if (participantFG.value.selection !== IRespondentSelectionTypes.StateOfIllinois) {
      participantFG.controls.attorneys.addValidators([Validators.required, minActive(1)]);
      participantFG.controls.attorneys.updateValueAndValidity();
      participantFG.updateValueAndValidity();
    }
  }

  /**
   * Add a surviving dependant to the participant form group.
   * @param participantFG
   */
  addSurvivingDependant(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    participantFG.controls.claimantValues.controls.survivingDependants.push(new FormControl('', {nonNullable: true}));
  }

  /**
   * Add a surviving dependant to the participant form group.
   * @param participantFG
   */
  addWarrantNumber(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    participantFG.controls.claimantValues.controls.warrantNums.push(new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.pattern(ALPHA_NUMERIC_REGEX),
        Validators.maxLength(20)
      ]
    }));
  }

  /**
   * Remove a surviving dependant from the participant form group.
   * @param participantFG
   * @param index
   */
  removeSurvivingDependant(participantFG: FormGroup<ICaseParticipantFormGroup>, index: number): void {
    participantFG.controls.claimantValues.controls.survivingDependants.removeAt(index);
  }

  /**
   * Remove a warrant number from the participant form group.
   * @param participantFG
   * @param index
   */
  removeWarrantNumber(participantFG: FormGroup<ICaseParticipantFormGroup>, index: number): void {
    participantFG.controls.claimantValues.controls.warrantNums.removeAt(index);
  }

  /**
   * Remove the attorney validators from the participant form group.
   * @param participantFG
   */
  removeAttorneyValidators(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    // Remove validators from the attorneys FormArray itself
    participantFG.controls.attorneys?.clearValidators();
    participantFG.controls.attorneys?.updateValueAndValidity();

    // Remove validators from each control inside each attorney FormGroup
    if (participantFG.controls.attorneys) {
      (participantFG.controls.attorneys as FormArray).controls.forEach(attorneyFG => {
        const group = attorneyFG as FormGroup;
        Object.values(group.controls).forEach(control => {
          control.clearValidators();
          control.updateValueAndValidity();
        });
      });
    }

    participantFG.updateValueAndValidity();
  }

  /**
   * Delete a case participant from the form array.
   * @param caseParticipantFG
   */
  deleteCaseParticipant(caseParticipantFG: FormGroup<ICaseParticipantFormGroup>): void {
    // Mark the participant and their attorneys as inactive
    caseParticipantFG.controls.status.setValue(ParticipantStatus.Inactive);
    caseParticipantFG.controls.attorneys.controls.forEach(attorney => {
      attorney.controls.status.setValue(ParticipantStatus.Inactive);
    });
    // We marked the participant as inactive, so we need to clear the required validators since by default, the participant
    // is going to remain in the array, just marked as inactive.
    this.formUtilsService.clearValidators([
      caseParticipantFG.controls.address.controls.address1,
      caseParticipantFG.controls.address.controls.city,
      caseParticipantFG.controls.address.controls.state,
      caseParticipantFG.controls.address.controls.zip,
      // All the required claimant values
      caseParticipantFG.controls.claimantValues.controls.race,
      caseParticipantFG.controls.claimantValues.controls.phoneNum,
      caseParticipantFG.controls.claimantValues.controls.gender,
      caseParticipantFG.controls.claimantValues.controls.ageRange,
      caseParticipantFG.controls.claimantValues.controls.numberOfMinors,
      caseParticipantFG.controls.claimantValues.controls.crimeVictimName
    ], true);
    // Remove the required validators for attorneys and update the FormGroup Validity
    this.removeAttorneyValidators(caseParticipantFG);
  }

  /**
   * Add an attorney to the participant form group.
   * @param participant
   * @param attorney
   */
  addParticipantAttorney(participant: FormGroup<ICaseParticipantFormGroup>, attorney?: CaseParticipantAttorneyDTO): void {
    let fullName = attorney?.fName;
    fullName += !!attorney?.mName ? ` ${attorney?.mName}` : '';
    fullName += !!attorney?.lName ? ` ${attorney?.lName}` : '';

    participant.controls.attorneys.push(this.fb.group({
      id: new FormControl(attorney?.id || null, {nonNullable: true}),
      attorneyId: new FormControl(attorney?.attorneyId || null, {nonNullable: true, validators: Validators.required}),
      attorneyIdOption: new FormControl<string | FsxOption<string>>(attorney?.attorneyId || '', {nonNullable: true, validators: Validators.required}),
      userId: new FormControl(attorney?.userId || null, {nonNullable: true}),
      name: new FormControl(fullName || '', {nonNullable: true}),
      firstName: new FormControl(attorney?.fName || ''),
      middleName: new FormControl(attorney?.mName || ''),
      lastName: new FormControl(attorney?.lName || ''),
      email: new FormControl(attorney?.email || '', {nonNullable: true}),
      firmName: new FormControl(attorney?.firmName || '', {nonNullable: true}),
      address: this.fb.group({
        attention: new FormControl(attorney?.address?.attention || ''),
        address1: new FormControl(attorney?.address?.address1 || '', {nonNullable: true}),
        address2: new FormControl(attorney?.address?.address2 || ''),
        city: new FormControl(attorney?.address?.city || '', {nonNullable: true}),
        state: new FormControl(attorney?.address?.state || '', {nonNullable: true}),
        zip: new FormControl(attorney?.address?.zip || '', {nonNullable: true}),
      }  as IPostFilingAddressFormGroup),
      phoneNumber: new FormControl(attorney?.phoneNumber || '', {nonNullable: true}),
      status: new FormControl(attorney?.status || ParticipantStatus.Active, {nonNullable: true}),
    } as ICaseParticipantAttorneyFormGroup));
  }

  /**
   * Add an attorney to the participant form group.
   * @param participant
   * @param user
   */
  addParticipantAttorneyViaUserModel(participant: FormGroup<ICaseParticipantFormGroup>, user?: UserModel): void {
    let fullName = user?.firstName;
    fullName += !!user?.middleName ? ` ${user?.middleName}` : '';
    fullName += !!user?.lastName ? ` ${user?.lastName}` : '';

    participant.controls.attorneys.push(this.fb.group({
      id: new FormControl(user?.id || null, {nonNullable: true}),
      attorneyId: new FormControl(user?.attorneyInfo?.id || null, {nonNullable: true, validators: Validators.required}),
      attorneyIdOption: new FormControl<string | FsxOption<string>>(user?.attorneyInfo?.id || '', {nonNullable: true, validators: Validators.required}),
      userId: new FormControl(user?.id || null, {nonNullable: true}),
      name: new FormControl(fullName || '', {nonNullable: true}),
      firstName: new FormControl(user?.firstName || ''),
      middleName: new FormControl(user?.middleName || ''),
      lastName: new FormControl(user?.lastName || ''),
      email: new FormControl(user?.emailAddress || '', {nonNullable: true}),
      firmName: new FormControl(user?.attorneyInfo?.organizationName || '', {nonNullable: true}),
      address: this.fb.group({
        // ToDo: 2/24/2025 - Check with Ryan and find out if this will/needs to be included in the user model.
        attention: new FormControl(''),
        address1: new FormControl(user?.address1 || '', {nonNullable: true}),
        address2: new FormControl(user?.address2 || ''),
        city: new FormControl(user?.city || '', {nonNullable: true}),
        state: new FormControl(user?.state || '', {nonNullable: true}),
        zip: new FormControl(user?.zip || '', {nonNullable: true}),
      }  as IPostFilingAddressFormGroup),
      phoneNumber: new FormControl(user?.phoneNumber || '', {nonNullable: true}),
      status: new FormControl(ParticipantStatus.Active),
    } as ICaseParticipantAttorneyFormGroup));
  }

  /**
   * Delete an attorney to the participant form group
   * @param attorneyFG
   */
  deleteParticipantAttorney(attorneyFG: FormGroup<ICaseParticipantAttorneyFormGroup>): void {
    attorneyFG.controls.status.setValue(ParticipantStatus.Inactive);
  }

  /**
   * Copy assistant counsel from a form group to another
   * @param assistantCounselFG
   */
  copyAssistantCounselFG(assistantCounselFG: FormGroup<ICaseParticipantFormGroup>): FormGroup<ICaseParticipantFormGroup> {
    const fg = this.fb.group({
      id: new FormControl(DEFAULT_GUID, {nonNullable: true}),
      status: new FormControl(ParticipantStatus.Active, {nonNullable: true}),
      type: new FormControl(ParticipantType.Staff),
      name: new FormControl('', {nonNullable: true, validators: Validators.required}),
      address: this.fb.group({
        attention: new FormControl(''),
        address1: new FormControl('', {nonNullable: true}),
        address2: new FormControl(''),
        city: new FormControl('', {nonNullable: true}),
        state: new FormControl('', {nonNullable: true}),
        zip: new FormControl('', {nonNullable: true}),
      } as IPostFilingAddressFormGroup),
      userId: new FormControl('', {nonNullable: true}),
      attorneys: this.fb.array([] as FormGroup<ICaseParticipantAttorneyFormGroup>[]),
      isProse: new FormControl(false, {nonNullable: true}),
      isNAOF: new FormControl(false, {nonNullable: true}),
      inList: new FormControl(false, {nonNullable: true}),
      selection: new FormControl(IRespondentSelectionTypes.None, {nonNullable: true}),
      claimantValues: this.fb.group({} as ICaseClaimantFormGroup)
    }) as FormGroup<ICaseParticipantFormGroup>;
    fg.patchValue(assistantCounselFG.value);
    return fg;
  }

  /**
   * Add a main document to the document info form group.
   * @param mainDocument
   * @param uploadedFile
   */
  addMainDocument(mainDocument: FormGroup<IDocumentFormGroup>, uploadedFile: IUploadedFile): void {
    // Add missing Validators
    this.addMainDocumentValidators(mainDocument);
    // Set values
    mainDocument.controls.file.setValue(uploadedFile);
    mainDocument.controls.fileName.setValue(uploadedFile.file.name);
    mainDocument.controls.displayName.setValue(uploadedFile.file.name);
    mainDocument.controls.status.setValue(ParticipantStatus.Active);
    if (mainDocument.controls.description.value === '') {
      mainDocument.controls.description.setValue(uploadedFile.file.name.replace(/\.[^/.]+$/, ""));
    }
    mainDocument.updateValueAndValidity();
  }

  /**
   * Helper method to add missing validators to main document control.
   * @param mainDocumentFG
   */
  addMainDocumentValidators(mainDocumentFG: FormGroup<IDocumentFormGroup>): void {
    // Skip file required when document already exists (documentId or uri populated) — user shouldn't need to re-upload to save.
    const hasExistingDoc = !!mainDocumentFG.controls.documentId?.value || !!mainDocumentFG.controls.uri?.value;
    if (!hasExistingDoc) {
      mainDocumentFG.controls.file.addValidators(Validators.required);
    }
    mainDocumentFG.controls.fileName.addValidators(Validators.required);
    mainDocumentFG.controls.displayName.addValidators(Validators.required);
    mainDocumentFG.controls.description.addValidators(Validators.required);
  }

  /**
   * Helper method to remove validators from main document control.
   * @param mainDocumentFG
   */
  removeDocumentValidators(mainDocumentFG: FormGroup<IDocumentFormGroup>): void {
    mainDocumentFG.controls.file.removeValidators(Validators.required);
    mainDocumentFG.controls.fileName.removeValidators(Validators.required);
    mainDocumentFG.controls.displayName.removeValidators(Validators.required);
    mainDocumentFG.controls.description.removeValidators(Validators.required);
    mainDocumentFG.updateValueAndValidity();
  }

  /**
   * Reset document form group.
   * @param mainDocument
   */
  deleteMainDocument(mainDocument: FormGroup<IDocumentFormGroup>): void {
    mainDocument.reset();
  }

  /**
   * Add a supporting document form group to the docket entry form array.
   * @param supportingDocsFA
   */
  addSupportingDocument(supportingDocsFA: FormArray<FormGroup<IDocumentFormGroup>>): void {
    supportingDocsFA.push(this.fb.group({
      status: new FormControl(ParticipantStatus.Active, {nonNullable: true}),
      documentId: new FormControl('', {nonNullable: true}),
      file: new FormControl(null, {nonNullable: false, validators: [Validators.required, documentMimeTypeValidator(ALLOWED_MIME_TYPES), documentMaxSizeValidator(MAX_FILE_SIZE)]}),
      fileName: new FormControl('', {nonNullable: true, validators: Validators.required}),
      displayName: new FormControl('', {nonNullable: true, validators: Validators.required}),
      description: new FormControl('', {nonNullable: true, validators: Validators.required}),
      docTypeId: new FormControl(null, {nonNullable: true, validators: Validators.required}),
      // Empty placeholder (supporting documents have no determination); detect it with hasDeterminationForm().
      determination: this.fb.group({} as IDeterminationFormGroup),
    }) as FormGroup<IDocumentFormGroup>);
  }

  /**
   * Upload supporting documents to a specific document form control. If more than one file included, creates new form controls.
   * @param supportingDocumentsFA
   * @param supportingDocFC
   * @param files
   */
  uploadSupportingDocuments(supportingDocumentsFA: FormArray<FormGroup<IDocumentFormGroup>>, supportingDocFC: FormGroup<IDocumentFormGroup>, files: IUploadedFile[]): void {
    files.forEach((file, i) => {
      if (i === 0) {
        supportingDocFC.controls.file.setValue(file);
        supportingDocFC.controls.fileName.setValue(file.file.name);
        supportingDocFC.controls.displayName.setValue(file.file.name);
        if (supportingDocFC.controls.description.value === '') {
          supportingDocFC.controls.description.setValue(file.file.name.replace(/\.[^/.]+$/, ""));
        }
      } else {
        this.addSupportingDocument(supportingDocumentsFA);
        const lastSupDoc = supportingDocumentsFA.at(supportingDocumentsFA.length - 1) as FormGroup<IDocumentFormGroup>;
        lastSupDoc.controls.file.setValue(file);
        lastSupDoc.controls.fileName.setValue(file.file.name);
        lastSupDoc.controls.displayName.setValue(file.file.name);
        if (lastSupDoc.controls.description.value === '') {
          lastSupDoc.controls.description.setValue(file.file.name.replace(/\.[^/.]+$/, ""));
        }
      }
    });
  }

  /**
   * Delete a supporting document form group from the docket entry form array.
   * @param supportingDocsFA
   * @param index
   */
  deleteSupportingDocument(supportingDocsFA: FormArray<FormGroup<IDocumentFormGroup>>, index: number): void {
    supportingDocsFA.at(index).controls.status.setValue(ParticipantStatus.Inactive);
  }

  /**
   * Clears the name of the supporting document form control.
   * @param supportingDocFC
   */
  clearSupportingDocumentName(supportingDocFC: FormGroup<IDocumentFormGroup>): void {
    supportingDocFC.controls.displayName.reset();
    supportingDocFC.controls.displayName.setValue('');
  }

  /**
   * Build the docket entry DTOs for new entries.
   * @param entryFG
   */
  buildDocketEntryDocumentDTOs(entryFG: FormGroup<ICaseDocketEntryFormGroup>): Observable<PostFilingDocDTO[]> {
    // Main document DTO Observable
    const mainDocDTO$: Observable<PostFilingDocDTO> =  !!entryFG.controls.mainDocument.controls.file.value ?
      this.buildNewDocumentDTO(entryFG.controls.mainDocument, true) :
      this.buildDraftDocumentDTO(entryFG.controls.mainDocument, true)
    ;
    // Supporting documents DTO Observables
    const supportingDocsDTOs$: Observable<PostFilingDocDTO>[] = [];
    entryFG.controls.supportingDocuments.controls.forEach(supportingDocFG => {
      const supportingDocDTO: Observable<PostFilingDocDTO> = !!supportingDocFG.controls.file.value ?
        this.buildNewDocumentDTO(supportingDocFG, false) :
        this.buildDraftDocumentDTO(supportingDocFG, false)
      ;
      supportingDocsDTOs$.push(supportingDocDTO);
    });
    return forkJoin([mainDocDTO$, ...supportingDocsDTOs$]);
  }

  /**
   * Private helper method to build the DTO for a document with no new file to upload (a saved document or an empty
   * slot). A DL/IR determination on the document is sent with it; the empty placeholder determination group the
   * builders seed (buildCaseDocketEntryFromDTO, addSupportingDocument) is skipped (hasDeterminationForm), so no empty
   * determination is sent.
   * @param documentFG the document form group
   * @param isMain whether it is the main document
   * @returns the document DTO
   * @private
   */
  private buildDraftDocumentDTO(documentFG: FormGroup<IDocumentFormGroup>, isMain: boolean): Observable<PostFilingDocDTO> {
    const postFilingDocDTO: PostFilingDocDTO = {
      documentId: documentFG.controls.documentId.value || null,
      mainDocument: isMain,
      fileName: documentFG.controls.fileName.value,
      displayName: documentFG.controls.displayName.value,
      description: documentFG.controls.description.value,
      fileURI: documentFG.controls.uri?.value || '',
      status: Number(documentFG.controls.status.value),
      docType: !documentFG.value.docTypeId ? null : documentFG.value.docTypeId
    };
    if (hasDeterminationForm(documentFG)) {
      this.buildDraftDeterminationDTO(documentFG, postFilingDocDTO);
    }
    return of(postFilingDocDTO);
  }

  /**
   * Adds the DL/IR determination to a docket entry document DTO: the category, the determination, the checked
   * victimization types, the award amounts above zero and the supplemental number. Used for already-saved documents
   * (buildDraftDocumentDTO) and new uploads (buildNewDocumentDTO).
   * @param documentFG the document form group holding the determination
   * @param postFilingDocDTO the document DTO to add the determination to
   * @private
   */
  private buildDraftDeterminationDTO(documentFG: FormGroup<IDocumentFormGroup>, postFilingDocDTO: PostFilingDocDTO): void {
    let victimTypes: string[] = [];
    if (!!documentFG.value.determination?.victimizationTypes && documentFG.value.determination.victimizationTypes.length) {
      victimTypes = documentFG.value.determination?.victimizationTypes
        .filter(v => v.checked)
        .map(v => v.value) as string[]
      ;
    }

    let awardValues: DeterminationAwardOfGuid[] = [];
    if (!!documentFG.value.determination?.awardTypes && documentFG.value.determination.awardTypes.length) {
      awardValues = documentFG.value.determination?.awardTypes
        .filter(v => !!v.amount && v.amount > 0)
        .map(v => {
          return {
            award: v.typeId,
            awardValue: v.amount
          } as DeterminationAwardOfGuid;
        }) as DeterminationAwardOfGuid[]
      ;
    }

    postFilingDocDTO.determination = {
      determinationCategoryId: documentFG.value.determination?.category !== '' ? documentFG.value.determination?.category : null,
      determinationId: documentFG.value.determination?.determination !== '' ? documentFG.value.determination?.determination : null,
      determinationVictimTypes: victimTypes,
      determinationAwards: awardValues,
      supplementalDeterminationNum: documentFG.value.determination?.supplementalNumber === -1 ? null : documentFG.value.determination?.supplementalNumber
    };
  }

  /**
   * Private helper method to build a document DTO. Will first write the file and then return the DTO with the appropriate URI.
   * A DL/IR determination on the document is sent too, the same as for an already-saved document; the empty placeholder
   * determination group that supporting documents carry is skipped (hasDeterminationForm).
   * @param documentFG the document form group
   * @param isMain whether it is the main document
   * @returns the document DTO, once the file is uploaded
   * @private
   */
  private buildNewDocumentDTO(documentFG: FormGroup<IDocumentFormGroup>, isMain: boolean): Observable<PostFilingDocDTO> {
    return this.documentApiService.getDocumentUri().pipe(
      take(1),
      switchMap(uri => {
        if (!!documentFG.controls.file.value) {
          const file = documentFG.controls.file.value.file;
          return this.documentApiService.uploadDocument2(uri.uri, file).pipe(
            map(r => {
              const postFilingDocDTO: PostFilingDocDTO = {
                documentId: documentFG.controls.documentId.value || null,
                mainDocument: isMain,
                fileName: documentFG.controls.fileName.value,
                displayName: documentFG.controls.displayName.value,
                description: documentFG.controls.description.value,
                fileURI: uri.uri,
                status: documentFG.controls.status.value,
                docType: !documentFG.value.docTypeId ? null : documentFG.value.docTypeId
              };
              if (hasDeterminationForm(documentFG)) {
                this.buildDraftDeterminationDTO(documentFG, postFilingDocDTO);
              }
              return postFilingDocDTO;
            }))
            ;
        }
        return EMPTY;
      }),
      catchError(err => {
        console.error('Error occurred setting DTO:', err);
        return EMPTY;
      }))
    ;
  }

  /**
   * Clears the email of a participant.
   * @param participantFG
   */
  clearParticipantEmail(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    participantFG.controls.claimantValues.controls.email.setValue('');
  }

  /**
   * Clears the phone number of a participant.
   * @param participantFG
   */
  clearParticipantPhone(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    participantFG.controls.claimantValues.controls.phoneNum.setValue('');
  }

  /**
   * Clears the Claimant Values of the Participant.
   * @param participantFG
   */
  clearParticipantClaimantValues(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    participantFG.controls.claimantValues.reset();
    participantFG.controls.claimantValues.controls.ssn.setValue('');
  }

  /**
   * Clears the address of a participant.
   * @param participantFG
   */
  clearParticipantAddress(participantFG: FormGroup<ICaseParticipantFormGroup>): void {
    participantFG.controls.address.controls.attention.setValue('');
    participantFG.controls.address.controls.address1.setValue('');
    participantFG.controls.address.controls.address2.setValue('');
    participantFG.controls.address.controls.city.setValue('');
    participantFG.controls.address.controls.state.setValue('');
    participantFG.controls.address.controls.zip.setValue('');
  }

  /**
   * Set the address of a participant from a user model.
   * @param participantFG
   * @param user
   */
  setParticipantAddressFromUser(participantFG: FormGroup<ICaseParticipantFormGroup>, user: UserModel): void {
    participantFG.controls.address.controls.address1.setValue(user.address1 || '');
    participantFG.controls.address.controls.address2.setValue(user.address2 || '');
    participantFG.controls.address.controls.city.setValue(user.city || '');
    participantFG.controls.address.controls.state.setValue(user.state || '');
    participantFG.controls.address.controls.zip.setValue(user.zip || '');
  }

  /**
   * Set the phone number of a participant from a user model.
   * @param participantFG
   * @param user
   */
  setParticipantPhoneFromUser(participantFG: FormGroup<ICaseParticipantFormGroup>, user: UserModel): void {
    participantFG.controls.claimantValues.controls.phoneNum.setValue(user.phoneNumber || '');
  }

  /**
   * Set the email of a participant from a user model.
   * @param participantFG
   * @param user
   */
  setParticipantEmailFromUser(participantFG: FormGroup<ICaseParticipantFormGroup>, user: UserModel): void {
    participantFG.controls.claimantValues.controls.email.setValue(user.emailAddress || '');
  }

  /**
   * Remove an invoice number from the case info nums form array.
   * @param caseFG
   */
  addInvoiceNumber(caseFG: FormGroup<ICaseFormGroup>): void {
    caseFG.controls.caseInfo.controls.invoiceNums.push(new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.pattern(ALPHA_NUMERIC_REGEX),
        Validators.maxLength(20)
      ]
    }));
  }

  /**
   * Remove an invoice number from the case info nums form array.
   * @param caseFG
   * @param index
   */
  removeInvoiceNumber(caseFG: FormGroup<ICaseFormGroup>, index: number): void {
    caseFG.controls.caseInfo.controls.invoiceNums.removeAt(index);
  }

  /**
   * Clears the file validators
   * @param fileFC
   */
  public clearFileValidators(fileFC: FormControl): void {
    fileFC.clearValidators();
    fileFC.updateValueAndValidity();
  }

  /**
   * Adds the file validators. Defaults to the default allowed mime types.
   * @param fileFC
   * @param allowedMimeTypes
   */
  public addFileValidators(fileFC: FormControl, allowedMimeTypes: string[] = ALLOWED_MIME_TYPES): void {
    // Skip required when parent doc FG already has documentId or uri (existing doc — no re-upload required).
    const parent = fileFC.parent as FormGroup | null;
    const hasExistingDoc = !!parent?.get('documentId')?.value || !!parent?.get('uri')?.value;
    const validators = hasExistingDoc
      ? [documentMimeTypeValidator(allowedMimeTypes), documentMaxSizeValidator(MAX_FILE_SIZE)]
      : [Validators.required, documentMimeTypeValidator(allowedMimeTypes), documentMaxSizeValidator(MAX_FILE_SIZE)];
    fileFC.addValidators(validators);
    fileFC.updateValueAndValidity();
  }

  /**
   * Remove the determination form group from the main document form group.
   * @param mainDocumentFG
   */
  public removeDeterminationFormGroup(mainDocumentFG: FormGroup<IDocumentFormGroup>): void {
    mainDocumentFG.removeControl('determination');
  }

  /**
   * Initialize the determination form group within the main document form group.
   * @param mainDocumentFG
   */
  public initDeterminationFormGroup(mainDocumentFG: FormGroup<IDocumentFormGroup>): void {
    mainDocumentFG.controls.determination = this.fb.group({
      category: new FormControl('', {validators: Validators.required}),
      supplementalNumber: new FormControl(-1, {nonNullable: true}),
      determination: new FormControl(null, {validators: Validators.required}),
      victimizationTypes: this.fb.array([] as FormGroup<IDeterminationVictimizationTypeFormGroup>[], {validators: minVictimizationTypeSelected(1)}),
      awardTypes: this.fb.array([] as FormGroup<IDeterminationExpenseFormGroup>[]),
      totalAwardAmount: new FormControl(0, {nonNullable: true}),
    } as IDeterminationFormGroup);
  }
}

/**
 * The master form group for the case form.
 */
export interface ICaseFormGroup {
  id: FormControl<string>;
  caseNum: FormControl<string>;
  caseName: FormControl<string>;
  caseInfo: FormGroup<ICaseGeneralInfoFormGroup>
  consolidation: FormGroup<ICaseConsolidationFormGroup>
  parties: FormGroup<ICasePartiesFormGroup>;
}

/**
 * The general information form group for the case form.
 */
export interface ICaseGeneralInfoFormGroup {
  voucherAwarded: FormControl<boolean>;
  voucherAwardedDate: FormControl<string | null>;
  voucherAwardedAmount: FormControl<number | null>;
  status: FormControl<CaseStatus>;
  presidingJudge: FormGroup<ICaseParticipantFormGroup>;
  staff: FormArray<FormGroup<ICaseParticipantFormGroup>>;
  caseType: FormGroup<ICaseDataItemFormGroup>;
  county: FormGroup<ICaseDataItemFormGroup>;
  claimType: FormGroup<ICaseDataItemFormGroup>;
  dateFiled: FormControl<string | null>;
  dateClosed: FormControl<string | null>;
  dateReopened: FormControl<string | null>;
  incidentDate: FormControl<string | null>;
  suspenseDate: FormControl<string | null>;
  claimAmount: FormControl<string>;
  agency: FormGroup<ICaseDataItemFormGroup>;
  subStatus: FormGroup<ICaseDataItemFormGroup>;
  prison: FormGroup<ICaseDataItemFormGroup>;
  feeWaiverApplied: FormControl<boolean | null>;
  invoiceNums: FormArray<FormControl<string | null>>;
}

/**
 * The consolidation form group for the case form.
 */
export interface ICaseConsolidationFormGroup {
  isConsolidated: FormControl<boolean>;
  consolidatedCaseNum: FormControl<string>;
}

/**
 * The parties form group for the case form.
 */
export interface ICasePartiesFormGroup {
  claimants: FormArray<FormGroup<ICaseParticipantFormGroup>>;
  respondents: FormArray<FormGroup<ICaseParticipantFormGroup>>;
}

/**
 * The program area form group for the case general info form group.
 */
export interface ICaseDataItemFormGroup {
  name: FormControl<string>,
  id: FormControl<string>,
}

/**
 * A formgroup that holds all specific claimant values. If needed, will create a separate one for respondent.
 */
export interface ICaseClaimantFormGroup {
  ageRange: FormControl<string | null>;
  county: FormGroup<ICountyFormGroup>;
  crimeVictimName: FormControl<string>;
  decedent: FormControl<string | null>;
  email: FormControl<string>;
  gender: FormControl<string | null>;
  numberOfMinors: FormControl<number | null>;
  prisonerNum: FormControl<string | null>;
  phoneNum: FormControl<string>;
  race: FormControl<string | null>;
  ssn: FormControl<string>;
  ssnRedacted: FormControl<string>;
  survivingDependantName: FormControl<string>;
  survivingDependants: FormArray<FormControl<string | null>>;
  // ToDo: 2/25/2025 - Need to convert to FormArray
  warrantNum: FormControl<string | null>;
  warrantNums: FormArray<FormControl<string | null>>;
}

/**
 * The form group interface for a case participant
 */
export interface ICaseParticipantFormGroup {
  id: FormControl<string | null>;
  status: FormControl<ParticipantStatus>;
  type: FormControl<ParticipantType>;
  name: FormControl<string>;
  address: FormGroup<IPostFilingAddressFormGroup>;
  userId: FormControl<string>;
  attorneys: FormArray<FormGroup<ICaseParticipantAttorneyFormGroup>>;
  isProse: FormControl<boolean>;
  isNAOF: FormControl<boolean>;
  inList: FormControl<boolean>;
  selection: FormControl<IRespondentSelectionTypes>;
  claimantValues: FormGroup<ICaseClaimantFormGroup>;
}

/**
 * The form group interface for a post filing address
 */
export interface IPostFilingAddressFormGroup {
  attention: FormControl<string>;
  address1: FormControl<string>;
  address2: FormControl<string>;
  city: FormControl<string>;
  state: FormControl<string>;
  zip: FormControl<string>;
}

/**
 * Interface representing a participant's attorney.
 * ToDo: Include Id and missing properties from DTO
 */
export interface ICaseParticipantAttorneyFormGroup {
  id: FormControl<string>;
  attorneyId: FormControl<string>;
  attorneyIdOption: FormControl<string | FsxOption<string>>;
  userId: FormControl<string>;
  name: FormControl<string>;
  firstName: FormControl<string>,
  middleName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  firmName: FormControl<string>;
  address: FormGroup<IPostFilingAddressFormGroup>;
  phoneNumber: FormControl<string>;
  status: FormControl<ParticipantStatus | null>;
}

/**
 * Interface representing a case entry form group.
 */
export interface ICaseDocketEntryFormGroup {
  filingId: FormControl<string | null>;
  filingDateTime: FormControl<string>;
  description: FormControl<string>;
  mainDocument: FormGroup<IDocumentFormGroup>;
  supportingDocuments: FormArray<FormGroup<IDocumentFormGroup>>;
  ehbCode: FormControl<string>;
  commonwealthCode: FormControl<string>;
}
/************************************************************************************************************
 * Case Details Partials
 *************************************************************************************************************/
// Deep Partial for ICaseFormGroup values
export interface CaseFormValues {
  id?: string;
  caseNum?: string;
  caseName?: string;
  caseInfo?: CaseGeneralInfoValues;
  consolidation?: CaseConsolidationValues;
  parties?: CasePartiesValues;
}

export interface CaseGeneralInfoValues {
  voucherAwarded?: boolean;
  voucherAwardedDate?: string | null;
  voucherAwardedAmount?: number | null;
  status?: CaseStatus;
  presidingJudge?: CaseParticipantValues;
  staff?: CaseParticipantValues[];
  caseType?: CaseDataItemValues;
  county?: CaseDataItemValues;
  claimType?: CaseDataItemValues;
  dateFiled?: string | null;
  dateClosed?: string | null;
  dateReopened?: string | null;
  incidentDate?: string | null;
  suspenseDate?: string | null;
  claimAmount?: string;
  agency?: CaseDataItemValues;
  subStatus?: CaseDataItemValues;
  prison?: CaseDataItemValues;
  feeWaiverApplied?: boolean | null;
  invoiceNums?: (string | null)[];
}

export interface CaseConsolidationValues {
  isConsolidated?: boolean;
  consolidatedCaseNum?: string;
}

export interface CasePartiesValues {
  claimants?: CaseParticipantValues[];
  respondents?: CaseParticipantValues[];
}

export interface CaseDataItemValues {
  name?: string;
  id?: string;
}

export interface CaseClaimantValues {
  ageRange?: string | null;
  county?: CountyValues;
  crimeVictimName?: string;
  decedent?: string | null;
  email?: string;
  gender?: string | null;
  numberOfMinors?: number | null;
  prisonerNum?: string | null;
  phoneNum?: string;
  race?: string | null;
  ssn?: string;
  ssnRedacted?: string;
  survivingDependantName?: string;
  survivingDependants?: (string | null)[];
  warrantNum?: string | null;
  warrantNums?: (string | null)[];
}

export interface CaseParticipantValues {
  id?: string | null;
  status?: ParticipantStatus;
  type?: ParticipantType;
  name?: string;
  address?: PostFilingAddressValues;
  userId?: string;
  attorneys?: CaseParticipantAttorneyValues[];
  isProse?: boolean;
  inList?: boolean;
  selection?: IRespondentSelectionTypes;
  claimantValues?: CaseClaimantValues;
}

export interface PostFilingAddressValues {
  attention?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface CaseParticipantAttorneyValues {
  id?: string;
  attorneyId?: string;
  userId?: string;
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  firmName?: string;
  address?: PostFilingAddressValues;
  phoneNumber?: string;
  status?: ParticipantStatus | null;
}

export interface CountyValues {
  name?: string;
  id?: string;
}


/************************************************************************************************************
 * Docket Entry Partials
 *************************************************************************************************************/
// Deep Partial for ICaseDocketEntryFormGroup values
export interface CaseDocketEntryValues {
  filingId?: string | null;
  filingDateTime?: string;
  description?: string;
  mainDocument?: DocumentValues | null;
  supportingDocuments?: DocumentValues[];
  ehbCode?: string;
  commonwealthCode?: string;
}

export interface DocumentValues {
  documentId?: string;
  file?: any;
  fileName?: string;
  displayName?: string;
  description?: string;
  uri?: string;
  status?: ParticipantStatus;
  docTypeId?: string | null;
  determination?: DeterminationValues;
}

export interface DeterminationValues {
  category?: string;
  supplementalNumber?: number;
  determination?: string | null;
  victimizationTypes?: DeterminationVictimizationTypeValues[];
  awardTypes?: DeterminationExpenseValues[];
  totalAwardAmount?: number;
}

export interface DeterminationVictimizationTypeValues {
  value?: string;
  checked?: boolean;
  name?: string;
}

export interface DeterminationExpenseValues {
  amount?: number;
  typeId?: string;
  name?: string;
}
